import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import client from '@/http/customer-client'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/use-toast'
import AuthShell from '@modules/manifera-user/resources/js/pages/customer-app/ev-journey/components/AuthShell'
import { Eye, EyeOff } from 'lucide-react'

const formSchema = z
  .object({
    password: z
      .string()
      .min(1, {
        message: i18next.t('validation.password.min', { min: 1 })
      })
      .min(4, {
        message: i18next.t('validation.password.min', { min: 4 })
      }),
    password_confirmation: z.string()
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: i18next.t('page_setup_password.form_schema_match_password'),
    path: ['password_confirmation']
  })

type SetupPasswordPayload = {
  token: string
  password: string
  password_confirmation: string
}

async function apiValidateSetupPasswordToken(token: string) {
  await client.post('/app-api/validate-setup-password-token', { token })
}

async function apiSetupPassword(payload: SetupPasswordPayload) {
  await client.post('/app-api/setup-password', payload)
}

export default function SetupPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token') || ''

  const [isTokenValid, setIsTokenValid] = useState<boolean>(false)
  const [isValidatingToken, setIsValidatingToken] = useState<boolean>(true)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState<boolean>(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      password_confirmation: ''
    }
  })

  useEffect(() => {
    if (token === '') {
      void navigate('/customer')
      return
    }

    const validateToken = async () => {
      try {
        await apiValidateSetupPasswordToken(token)
        setIsTokenValid(true)
      } catch {
        void navigate('/customer')
      } finally {
        setIsValidatingToken(false)
      }
    }

    void validateToken()
  }, [navigate, token])

  const setupPasswordMutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      await apiSetupPassword({
        token,
        password: values.password,
        password_confirmation: values.password_confirmation
      })
    },
    onSuccess: () => {
      toast({
        title: t('page_setup_password.toast_successful_title'),
        description: t('page_setup_password.toast_successful_description'),
        variant: 'success'
      })
      void navigate('/customer/login')
    },
    onError: () => {
      form.setError('root', {
        type: 'server',
        message: t('page_setup_password.server_error')
      })
    }
  })

  return (
    <AuthShell
      title={t('page_setup_password.title')}
      subtitle={t('page_setup_password.description')}
    >
      {isValidatingToken || !isTokenValid ? null : (
        <form
          className='space-y-4'
          onSubmit={(event) => {
            void form.handleSubmit((values) => {
              form.clearErrors()
              setupPasswordMutation.mutate(values)
            })(event)
          }}
        >
          <div className='space-y-2'>
            <Label htmlFor='password'>
              {t('page_setup_password.form_password')}
            </Label>
            <div className='relative'>
              <Input
                id='password'
                type={showPassword ? 'text' : 'password'}
                {...form.register('password')}
                className='h-12 pr-12'
                placeholder='Uw wachtwoord'
              />
              <button
                type='button'
                onClick={() => {
                  setShowPassword(!showPassword)
                }}
                className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2'
              >
                {showPassword ? (
                  <EyeOff className='h-5 w-5' />
                ) : (
                  <Eye className='h-5 w-5' />
                )}
              </button>
            </div>
            {form.formState.errors.password?.message && (
              <p className='text-destructive text-sm'>
                {form.formState.errors.password.message satisfies string}
              </p>
            )}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='password_confirmation'>
              {t('page_setup_password.form_password_confirmation')}
            </Label>
            <div className='relative'>
              <Input
                id='password_confirmation'
                type={showPasswordConfirmation ? 'text' : 'password'}
                {...form.register('password_confirmation')}
                className='h-12 pr-12'
                placeholder='Bevestig uw wachtwoord'
              />
              <button
                type='button'
                onClick={() => {
                  setShowPasswordConfirmation(!showPasswordConfirmation)
                }}
                className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2'
              >
                {showPasswordConfirmation ? (
                  <EyeOff className='h-5 w-5' />
                ) : (
                  <Eye className='h-5 w-5' />
                )}
              </button>
            </div>
            {form.formState.errors.password_confirmation?.message && (
              <p className='text-destructive text-sm'>
                {
                  form.formState.errors.password_confirmation
                    .message satisfies string
                }
              </p>
            )}
          </div>

          {form.formState.errors.root?.message && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.root.message}
            </p>
          )}

          <Button
            type='submit'
            className='w-full'
            disabled={setupPasswordMutation.isPending || token === ''}
          >
            {setupPasswordMutation.isPending
              ? t('page_setup_password.submitting')
              : t('button.submit')}
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
